import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Download, Loader2, Trash2, Upload } from 'lucide-react';
import dayjs from 'dayjs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/shared/DatePicker';
import { useEmployeesApi } from '@/services/api';
import { t } from '@/i18n';

interface StudentCardEditorProps {
  companyNip: string;
  employeeId: string;
  hasCard: boolean;
  expiryDate: string | null;
  onChange: () => void;
}

export function StudentCardEditor({ companyNip, employeeId, hasCard, expiryDate, onChange }: StudentCardEditorProps) {
  const { setStudentCard, deleteStudentCard, downloadStudentCard } = useEmployeesApi();
  const [file, setFile] = useState<File | null>(null);
  const [expiry, setExpiry] = useState<Date | null>(expiryDate ? new Date(expiryDate) : null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!file) {
      toast.error(t('employees.validation.studentCardRequired'));
      return;
    }
    if (!expiry) {
      toast.error(t('employees.employment.studentCard.expiryDate'));
      return;
    }
    setBusy(true);
    try {
      await setStudentCard(companyNip, employeeId, file, dayjs(expiry).format('YYYY-MM-DD'));
      toast.success(t('common.save') + ' ✓');
      resetFile();
      onChange();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteStudentCard(companyNip, employeeId);
      toast.success(t('common.delete') + ' ✓');
      onChange();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadStudentCard(companyNip, employeeId);
    } catch {
      /* session expired — handled by the global modal */
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" asChild>
          <label className="cursor-pointer">
            <Upload className="size-4" />
            {hasCard
              ? t('employees.employment.studentCard.replace')
              : t('employees.employment.studentCard.upload')}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </Button>
        {file && <span className="text-muted-foreground text-sm">{file.name}</span>}
        <DatePicker
          value={expiry}
          onChange={setExpiry}
          placeholder={t('employees.employment.studentCard.expiryDate')}
          className="w-[220px]"
        />
        <Button disabled={!file || !expiry || busy} onClick={handleSave}>
          {busy && <Loader2 className="size-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
      {hasCard && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={handleDownload}>
            <Download className="size-4" />
            {t('employees.employment.studentCard.download')}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                {t('employees.employment.studentCard.delete')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('employees.employment.studentCard.delete') + '?'}
                </AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/90"
                  onClick={handleDelete}
                >
                  {t('common.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
